// Reason options shown in the parent app booking dropdown. The first
// three are fixed category keys (lowercase, snake_case) sent verbatim as
// `parent_connect_reason`; "Others" reveals a free-text box whose value
// is sent in its place. The backend accepts any non-empty string, so
// these keys are a UX convention rather than a hard whitelist.
export const PARENT_CONNECT_REASON_OPTIONS = [
  { value: 'behavioural_discussion', label: 'Behavioural discussion' },
  { value: 'report_card_discussion', label: 'Report card discussion' },
  {
    value: 'general_performance_discussion',
    label: 'General performance discussion',
  },
  { value: 'others', label: 'Others' },
] as const;

export const RENEWAL_PARENT_CONNECT_REASON = 'parent_connect_for_renewal';
export const PTM_PARENT_CONNECT_REASON = 'module_report_card_discussion';

export type ParentConnectReason =
  | (typeof PARENT_CONNECT_REASON_OPTIONS)[number]['value']
  | typeof PTM_PARENT_CONNECT_REASON
  | typeof RENEWAL_PARENT_CONNECT_REASON;

// What the backend suggests pre-selecting: a pending renewal connect first,
// else a bookable module-report PTM. The parent can still pick another reason.
export type BookingSuggestion = {
  suggested_reason:
    | typeof RENEWAL_PARENT_CONNECT_REASON
    | typeof PTM_PARENT_CONNECT_REASON
    | null;
  renewal_pc_pending: boolean;
  ptm: {
    ptm_module_id: string;
    ptm_product_id: string;
    ptm_module_name: string;
    ptm_report_url: string;
  } | null;
};

export const NO_BOOKING_SUGGESTION: BookingSuggestion = {
  suggested_reason: null,
  renewal_pc_pending: false,
  ptm: null,
};

const PICKER_REASONS: ReadonlySet<string> = new Set([
  ...PARENT_CONNECT_REASON_OPTIONS.map(option => option.value),
  PTM_PARENT_CONNECT_REASON,
  RENEWAL_PARENT_CONNECT_REASON,
]);

// An existing request's reason as the picker shows it. A parent's own
// "Others" text comes back as free text; trainer-only keys such as
// `parent_requested` have no parent option and fall back to the default.
export const reasonFromExisting = (
  raw: string | null | undefined
): { reason: ParentConnectReason; otherText: string } | null => {
  const value = (raw ?? '').trim();
  if (value.length === 0) {
    return null;
  }
  if (PICKER_REASONS.has(value)) {
    return { reason: value as ParentConnectReason, otherText: '' };
  }
  // A snake_case key (e.g. `parent_requested`), not text a parent typed.
  if (value.includes('_') && /^[a-z_]+$/.test(value)) {
    return { reason: 'report_card_discussion', otherText: '' };
  }
  return { reason: 'others', otherText: value };
};

// The one category that unlocks the free-text reason box. When picked,
// the parent types a custom reason that is sent directly as
// `parent_connect_reason`.
export const OTHERS_PARENT_CONNECT_REASON: ParentConnectReason = 'others';

// Keep in sync with `_MAX_PARENT_CONNECT_REASON_WORDS` in
// `services/service-user/services/parent_connect_request_service.py`.
export const MAX_OTHER_REASON_WORDS = 100;

// Whitespace-delimited word count, matching the backend's `str.split()`
// semantics (empty / whitespace-only string -> 0 words).
export const countWords = (text: string): number => {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
};

// 15 and 30 min are the only durations service-user accepts
// (`duration_seconds in (900, 1800)`). The 45-min option in the design
// is intentionally omitted server-side: parent-connect sessions are
// product-capped at 30 minutes.
export const DURATION_OPTIONS = [
  { value: 900, label: '15 mins' },
  { value: 1800, label: '30 mins' },
] as const;

export type DurationSeconds = (typeof DURATION_OPTIONS)[number]['value'];

// Local thresholds for splitting the slot list into Day vs Evening
// buckets in the UI. Pure presentation - the server returns a flat
// list ordered by start_epoch and doesn't classify by part-of-day.
//
// "Day" covers slots whose local start hour is 06:00 .. 16:59;
// "Evening" picks up at 17:00 and runs through 05:59. We anchor on
// the slot's local hour (read off `start_time` "HH:MM" rather than
// re-computing from start_epoch) so DST transitions don't bounce a
// slot into the wrong bucket.
export const EVENING_START_HOUR_LOCAL = 17;
export const DAY_START_HOUR_LOCAL = 6;

export type Slot = {
  start_time: string; // "HH:MM" in student tz
  end_time: string; // "HH:MM" in student tz
  start_epoch: number;
  end_epoch: number;
};

export type GetSlotsResponse = {
  trainer_id: string;
  trainer_name?: string | null;
  trainer_type?: string | null;
  date: string; // "dd/MM/yyyy"
  student_timezone: string;
  slots: {
    '15_min': Slot[];
    '30_min': Slot[];
  };
  error?: string;
};

export type OverviewBatch = {
  batch_id?: string;
  batch_name?: string;
  batch_type?: string;
  course?: string;
  grade?: string;
  product_id?: string;
  variant_id?: string;
  schedule?: unknown;
};

export type OverviewTeacher = {
  teacher_id?: string;
  teacher_name?: string;
};

export type OverviewStudent = {
  profile_id?: string;
  student_name?: string;
  customer_timezone?: string;
};

export type GetOverviewResponse = {
  ptm_enabled?: boolean;
  ptm?: {
    ptm_module_id: string;
    ptm_product_id: string;
    ptm_module_name: string;
    ptm_booking_state: string;
    ptm_actions: string[];
    ptm_report_url: string;
  } | null;
  batch: OverviewBatch;
  teacher: OverviewTeacher;
  student: OverviewStudent;
  pending_parent_connect_requests: unknown[];
  approved_sessions: unknown[];
};

export type CreateRequestPayload = {
  tpc_type?: 'PTM';
  ptm_module_id?: string;
  ptm_product_id?: string;
  recent_pc_acknowledged?: boolean;
  variant_id: string;
  start_date: string; // "YYYY-MM-DD" in student tz
  start_time: string; // "HH:MM" in student tz
  duration_seconds: DurationSeconds;
  // Either a fixed dropdown category key or the free text the parent
  // typed under "Others" - sent straight through to the backend, which
  // accepts any non-empty value.
  parent_connect_reason: string;
};

export type CreateRequestResponse = {
  // service-user wraps the BSS row in `responseBody`; only fields the
  // UI actually reads after a successful submit are typed.
  session_id?: string;
  variant_id?: string;
  start_time?: number;
  end_time?: number;
  timeZone?: string;
  parent_connect_reason?: string;
};
